import React, { useState, useEffect } from 'react';
import { MapPin, Briefcase, Plus, Shield, Loader2, Edit2, Save, X } from 'lucide-react';
import { getCurrentTitle } from '../../../data/points';
import { sportEmojis } from '../../../lib/constants';
import { Member } from '@/types/member';
import ProfileImage from './ProfileImage';
import { useTheme } from '@/contexts/ThemeContext';
import SocialLinks from '../../social/SocialLinks';
import { SportsSelector } from './SportsSelector';
import { avatarCache } from '@/lib/utils/avatarCache';
import { cn } from '@/lib/utils';
import { CitySearchCombobox } from '../../ui/combobox/CitySearchCombobox';
import { useProfile } from '@/hooks/useProfile';
import { useNavigate, Link } from 'react-router-dom';
import { normalizeCity } from '@/lib/city/normalizer';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

interface ProfileHeaderProps {
  member: Member;
  cityImage: string;
  onImageChange?: (file: File) => void;
  profile?: any;
  socialLinks?: {
    linkedinUrl?: string | null;
    facebookUrl?: string | null;
    instagramUrl?: string | null;
  };
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  member,
  cityImage,
  onImageChange,
  profile,
  socialLinks,
}) => {
  const { isDarkMode } = useTheme();
  // Grab both updateProfile and fetchProfile
  const { updateProfile, fetchProfile } = useProfile();
  const [showSportsEditor, setShowSportsEditor] = useState(false);
  const navigate = useNavigate();
  const [isEditingCity, setIsEditingCity] = useState(false);
  const [cityError, setCityError] = useState<string | null>(null);
  const [isSavingCity, setIsSavingCity] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now());
  const [userClubs, setUserClubs] = useState<any[]>([]);
  const [isLoadingClubs, setIsLoadingClubs] = useState(false);
const [isEditingJob, setIsEditingJob] = useState(false);
const [jobTitle, setJobTitle] = useState(profile?.job_type || member.jobTitle || '');
const [company, setCompany] = useState(profile?.company || member.company || '');
const [isSavingJob, setIsSavingJob] = useState(false);
const [isEditingSocials, setIsEditingSocials] = useState(false);
const [linkedinUrl, setLinkedinUrl] = useState(profile?.linkedin_url || '');
const [facebookUrl, setFacebookUrl] = useState(profile?.facebook_url || '');
const [instagramUrl, setInstagramUrl] = useState(profile?.instagram_url || '');
const [isSavingSocials, setIsSavingSocials] = useState(false);
const [isEditMode, setIsEditMode] = useState(false);
const [completionPercentage, setCompletionPercentage] = useState(0);
const [completionDetails, setCompletionDetails] = useState<{
  hasSocialLinks: boolean;
  hasSports: boolean;
  hasCity: boolean;
  hasJobInfo: boolean;
  hasClubs: boolean;
}>({
  hasSocialLinks: false,
  hasSports: false,
  hasCity: false,
  hasJobInfo: false,
  hasClubs: false
});

  // Responsive layout helper
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Check screen size on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

useEffect(() => {
  setLinkedinUrl(profile?.linkedin_url || '');
  setFacebookUrl(profile?.facebook_url || '');
  setInstagramUrl(profile?.instagram_url || '');
}, [profile?.linkedin_url, profile?.facebook_url, profile?.instagram_url]);
const handleSaveSocialLinks = async () => {
  try {
    setIsSavingSocials(true);
    await updateProfile({
      linkedin_url: linkedinUrl,
      facebook_url: facebookUrl,
      instagram_url: instagramUrl
    });
    await fetchProfile();
    setIsEditingSocials(false);
  } catch (err) {
    console.error('Failed to update social links:', err);
  } finally {
    setIsSavingSocials(false);
  }
};

const handleSaveJobInfo = async () => {
  try {
    setIsSavingJob(true);
    await updateProfile({ job_type: jobTitle, company });
    await fetchProfile();
    setIsEditingJob(false);
  } catch (err) {
    console.error('Failed to update job info:', err);
  } finally {
    setIsSavingJob(false);
  }
};


  
  // Determine if this is _my_ profile (only my profile passes onImageChange)
  const isMyProfile = typeof onImageChange === 'function';
  // For member pages we still need the normalized city
  const canonicalCity = normalizeCity(profile?.city || member.city);

  // Fetch club ID and navigate
  const handleClubClick = async () => {
    if (profile?.club?.club_name) {
      try {
        const { data } = await supabase
          .from('clubs')
          .select('club_id')
          .eq('club_name', profile.club.club_name.trim())
          .single();
        if (data?.club_id) {
          navigate(`/dashboard/clubs/${data.club_id}`);
        }
      } catch (error) {
        console.error('Error fetching club ID:', error);
      }
    }
  };

  // Fetch user's clubs
  useEffect(() => {
    const fetchUserClubs = async () => {
      if (!profile?.id) return;
      try {
        setIsLoadingClubs(true);
        const { data: clubs, error: clubsError } = await supabase
          .from('user_clubs')
          .select(`
            id,
            is_primary,
            clubs (
              club_id,
              club_name,
              formatted_address,
              city,
              logo
            )
          `)
          .eq('user_id', profile.id);
        if (clubsError) throw clubsError;
        const transformed = clubs?.map((m) => ({
          club_id: m.clubs.club_id,
          club_name: m.clubs.club_name,
          formatted_address: m.clubs.formatted_address,
          city: m.clubs.city,
          logo: m.clubs.logo,
          is_primary: m.is_primary,
          user_club_id: m.id,
        })) || [];
        setUserClubs(transformed);
      } catch (err) {
        console.error('Error fetching user clubs:', err);
      } finally {
        setIsLoadingClubs(false);
      }
    };
    fetchUserClubs();
  }, [profile?.id]);

  // Handle city change: update server, re-fetch profile immediately, then close editor
  const handleCityChange = async (city: string) => {
    try {
      setIsSavingCity(true);
      setCityError(null);
      await updateProfile({ city });
      await fetchProfile();             // <-- immediate refresh
      setIsEditingCity(false);
      if (profile) profile.city = city;
    } catch (err) {
      console.error('Failed to update city:', err);
      setCityError('Failed to update city. Please try again.');
    } finally {
      setIsSavingCity(false);
    }
  };

  // Memoized title
  const currentTitle = React.useMemo(
    () => getCurrentTitle(member.points.total),
    [member.points.total]
  );

  // Update avatar cache on change
  useEffect(() => {
    if (profile?.avatar_url && member.id) {
      avatarCache.setAvatar(member.id, profile.avatar_url);
      setAvatarKey(Date.now());
    }
  }, [profile?.avatar_url, member.id]);

  // Memoized job info
  const jobInfo = React.useMemo(
    () => ({
      jobTitle: profile?.job_type || member.jobTitle || 'Role',
      company: profile?.company || member.company || 'Company',
    }),
    [profile?.job_type, profile?.company, member.jobTitle, member.company]
  );

  // Memoized sports transformation
  const userSports = React.useMemo(() => {
    const arr = Array.isArray(profile?.user_sports)
      ? profile.user_sports
      : [];
    if (arr.length) {
      return arr
        .filter((us: any) => us.sports?.name)
        .map((us: any) => ({
          name: us.sports.name,
          emoji: us.sports.emoji || '🎯',
        }));
    }
    return (member.sports || []).map((s: string) => ({
      name: s,
      emoji: sportEmojis[s.toLowerCase()] || '🎯',
    }));
  }, [profile?.user_sports, member.sports]);

  // Extract social links
  const finalSocialLinks = {
    linkedinUrl: socialLinks?.linkedinUrl || profile?.linkedin_url,
    facebookUrl: socialLinks?.facebookUrl || profile?.facebook_url,
    instagramUrl: socialLinks?.instagramUrl || profile?.instagram_url,
  };

  // Club display fallback
  const clubDisplay = profile?.club?.club_name || profile?.custom_club;

  // Calculate profile completion percentage
  useEffect(() => {
    let percentage = 0;
    
    // Social links - 20%
    const hasSocialLinks = Boolean(
      profile?.linkedin_url || profile?.facebook_url || profile?.instagram_url
    );
    if (hasSocialLinks) percentage += 20;
    
    // Sports - 20%
    const hasSports = Array.isArray(profile?.user_sports) && profile.user_sports.length > 0;
    if (hasSports) percentage += 20;
    
    // City - 20%
    const hasCity = Boolean(profile?.city);
    if (hasCity) percentage += 20;
    
    // Job title and company - 20%
    const hasJobInfo = Boolean(profile?.job_type && profile?.company);
    if (hasJobInfo) percentage += 20;
    
    // Clubs - 20%
    const hasClubs = userClubs.length > 0 || Boolean(profile?.club?.club_name || profile?.custom_club);
    if (hasClubs) percentage += 20;
    
    setCompletionPercentage(percentage);
    setCompletionDetails({
      hasSocialLinks,
      hasSports,
      hasCity,
      hasJobInfo,
      hasClubs
    });
  }, [
    profile?.linkedin_url, 
    profile?.facebook_url, 
    profile?.instagram_url,
    profile?.user_sports,
    profile?.city,
    profile?.job_type,
    profile?.company,
    profile?.club?.club_name,
    profile?.custom_club,
    userClubs.length
  ]);

  // Toggle edit mode
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  return (
    <div className="bg-dark-200 rounded-2xl overflow-hidden border border-dark-300">
      {/* Cityscape header */}
      <div className="relative h-64">
        <img
          src={cityImage}
          alt={`${member.city} cityscape`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-200 via-dark-200/40 to-transparent" />
      </div>

      {/* Main profile content */}
      <div className="relative px-6 pb-8 pt-2">
        <div className="flex flex-col items-center -mt-16">
          <ProfileImage
            src={profile?.avatar_url || member.image}
            key={avatarKey}
            alt={member.name}
            size={128}
            onImageChange={onImageChange}
            className="shadow-xl ring-4 ring-dark-200"
          />

          <h2 className="mt-4 text-2xl font-bold text-white">
            {profile?.full_name || member.name}
          </h2>
          
          {/* Profile Completion Indicator - Only show on own profile */}
          {isMyProfile && (
            <div className="mt-3 w-full max-w-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-dark-800">Profile Completion</span>
                <span className="text-xs text-gold-400 font-medium">{completionPercentage}%</span>
              </div>
              <Progress 
                value={completionPercentage} 
                className="h-1.5 bg-dark-300" 
              />
              {completionPercentage < 100 && (
                <div className="mt-2 text-xs text-dark-800">
                  {!completionDetails.hasSocialLinks && (
                    <div className="flex items-center gap-1 mb-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold-400"></div>
                      <span>Add social links</span>
                    </div>
                  )}
                  {!completionDetails.hasSports && (
                    <div className="flex items-center gap-1 mb-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold-400"></div>
                      <span>Add your sports</span>
                    </div>
                  )}
                  {!completionDetails.hasCity && (
                    <div className="flex items-center gap-1 mb-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold-400"></div>
                      <span>Add your city</span>
                    </div>
                  )}
                  {!completionDetails.hasJobInfo && (
                    <div className="flex items-center gap-1 mb-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold-400"></div>
                      <span>Add job title and company</span>
                    </div>
                  )}
                  {!completionDetails.hasClubs && (
                    <div className="flex items-center gap-1 mb-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold-400"></div>
                      <span>Add club membership</span>
                    </div>
                  )}
                </div>
              )}
              {completionPercentage === 100 && (
                <p className="text-xs text-gold-400 mt-1 text-center">
                  Your profile is complete! 🎉
                </p>
              )}
            </div>
          )}

          {/* Social links - Moved here between name and sports */}
          {isEditingSocials ? (
            <div className="mt-4 flex flex-col gap-3 text-sm text-white w-full max-w-xs sm:max-w-sm">
              <div className="flex items-center gap-2 w-full">
                <input
                  type="url"
                  placeholder="LinkedIn URL"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(e.target.value)}
                  className="bg-dark-300 border border-dark-400 rounded-lg px-3 py-2 text-white w-full"
                />
              </div>
              <div className="flex items-center gap-2 w-full">
                <input
                  type="url"
                  placeholder="Facebook URL"
                  value={facebookUrl}
                  onChange={(e) => setFacebookUrl(e.target.value)}
                  className="bg-dark-300 border border-dark-400 rounded-lg px-3 py-2 text-white w-full"
                />
              </div>
              <div className="flex items-center gap-2 w-full">
                <input
                  type="url"
                  placeholder="Instagram URL"
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  className="bg-dark-300 border border-dark-400 rounded-lg px-3 py-2 text-white w-full"
                />
              </div>
              <div className="flex gap-2 mt-1">
                <Button
                  onClick={() => setIsEditingSocials(false)}
                  variant="outline"
                  size="sm"
                  className="border-dark-400"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveSocialLinks}
                  disabled={isSavingSocials}
                  size="sm"
                  className="bg-gold-400 hover:bg-gold-500 text-dark-50"
                >
                  {isSavingSocials ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-2 relative">
              <div className="flex items-center justify-center">
                <SocialLinks
                  linkedinUrl={linkedinUrl}
                  facebookUrl={facebookUrl}
                  instagramUrl={instagramUrl}
                />
                {isMyProfile && isEditMode && (
                  <div className="flex items-center">
                    <button
                      onClick={() => setIsEditingSocials(true)}
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full bg-dark-300 text-gold-400 hover:bg-dark-400 ml-2"
                      )}
                      title="Edit social links"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sports badges */}
          <div className="mt-3 mb-3">
            {userSports.length > 0 ? (
              <div className="relative inline-block">
                <div className="flex flex-wrap justify-center gap-1.5 group">
                  {userSports.map((sport, idx) => (
                    <span
                      key={idx}
                      className={cn(
                        'inline-flex items-center px-2.5 py-1 rounded-full text-sm',
                        'bg-dark-300 text-white',
                        'transition-all duration-200',
                        'group-hover:opacity-90'
                      )}
                    >
                      {sport.emoji} {sport.name}
                    </span>
                  ))}
                  <button
                    onClick={() => setShowSportsEditor(true)}
                    className={cn(
                      'inline-flex items-center justify-center w-8 h-8 rounded-full bg-dark-300 text-gold-400 hover:text-gold-300 hover:bg-dark-400 transform hover:scale-110 active:scale-95 transition-all duration-200 shadow-lg'
                    )}
                    title="Edit sports"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowSportsEditor(true)}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-2 bg-dark-300 hover:bg-dark-400 text-gold-400 rounded-full transition-all duration-200 shadow-lg transform hover:scale-105 active:scale-95'
                )}
              >
                <Plus className="h-5 w-5" />
                <span>Add Sports</span>
              </button>
            )}
          </div>

          {/* City field: edit if mine, link if member */}
          <div className="flex items-center justify-center text-dark-800 mt-1">
            {isEditingCity ? (
              <div className="w-full max-w-xs sm:max-w-sm relative z-[60]">
                <CitySearchCombobox
                  value={profile?.city || member.city}
                  onChange={handleCityChange}
                  placeholder="Enter city..."
                  className={cn(
                    isDarkMode
                      ? 'bg-dark-300/50 border-dark-400/50 backdrop-blur-sm hover:bg-dark-300 hover:border-dark-400'
                      : 'bg-light-100/90 border-light-300 backdrop-blur-sm hover:bg-light-200 hover:border-light-400',
                    'focus-within:border-gold-400 transition-all duration-200',
                    cityError && '!border-red-500'
                  )}
                  error={cityError}
                />
                {isSavingCity && (
                  <div className="absolute inset-0 flex items-center justify-center bg-dark-300/50 backdrop-blur-sm rounded-lg">
                    <Loader2 className="animate-spin text-gold-400 h-5 w-5" />
                  </div>
                )}
                {cityError && (
                  <p className="absolute text-xs text-red-500 mt-1 left-0">
                    {cityError}
                  </p>
                )}
              </div>
            ) : (isMyProfile && isEditMode) ? (
              <div className="relative flex flex-wrap items-center justify-center gap-2">
                <div className={cn(
                  'flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg',
                 'bg-dark-300/50 text-white',
                 !isDarkMode && 'bg-light-300/90 text-light-900 border border-light-400'
                )}>
                  <MapPin className="h-5 w-5 text-gold-400" />
                  <span className="text-gold-400 font-medium mr-1 text-sm sm:text-base">Currently in:</span>
                  <span className="text-sm sm:text-base">{profile?.city || member.city}</span>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => setIsEditingCity(true)}
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-dark-300 text-gold-400 hover:bg-dark-400 ml-2"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : isMyProfile && !isEditMode ? (
              <button
                onClick={() => setIsEditingCity(true)}
                className={cn(
                  'flex items-center gap-2 px-4 py-1.5 rounded-full relative',
                  'border border-transparent hover:border-gold-400/30',
                  'focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400/50',
                  isDarkMode
                    ? 'bg-dark-300/50 hover:bg-dark-300 text-dark-800 hover:text-white'
                   : 'bg-light-300/90 hover:bg-light-400 text-light-800 hover:text-light-900 border-light-400',
                  'backdrop-blur-sm transition-all duration-200 group hover:shadow-md',
                  'hover:border-gold-400/30'
                )}
                disabled={isSavingCity}
                aria-label="Update your current city"
                title="Update when traveling to see nearby members"
              >
                <div className="relative">
                  <MapPin className="h-4 w-4 text-gold-400 group-hover:scale-125 transition-transform" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-gold-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                </div>
                <span className="relative flex items-center gap-1">
                  <span className="text-gold-400 font-medium">Currently in:</span>
                  {profile?.city || member.city}
                </span>
                <Edit2 className="h-3.5 w-3.5 ml-1 text-gold-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                
              </button>
            ) : (
              <div
                className={cn(
                  'flex items-center gap-2 px-4 py-1.5 rounded-full',
                  isDarkMode
                    ? 'bg-dark-300/50 hover:bg-dark-300 text-dark-800 hover:text-white border-transparent hover:border-dark-400'
                   : 'bg-light-300/90 hover:bg-light-400 text-light-800 hover:text-light-900 border border-light-400 hover:border-light-500',
                  'backdrop-blur-sm transition-all duration-200'
                )}
              >
                <MapPin className="h-4 w-4 text-gold-400 mr-1" />
                <span className="text-gold-400 font-medium mr-1">Currently in:</span>
                <Link
                  to="/dashboard/facilities"
                  state={{ selectedCity: canonicalCity }}
                  className="hover:text-gold-400 transition-colors"
                >
                  {profile?.city || member.city}
                </Link>
              </div>
            )}
          </div>

          {/* Job info */}
          <div className="flex items-center justify-center text-dark-800 mt-3 relative">
            {isEditingJob ? (
              <div className="flex flex-col items-center gap-3 w-full max-w-xs sm:max-w-sm">
                <div className="flex items-center gap-2 w-full">
                  <Briefcase className="h-5 w-5 text-gold-400 flex-shrink-0" />
                  <input
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="Job Title"
                    className="bg-dark-300 border border-dark-400 rounded-lg px-3 py-2 text-white w-full"
                  />
                </div>
                <div className="flex items-center gap-2 w-full">
                  <div className="w-5 flex-shrink-0"></div>
                  <input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Company"
                    className="bg-dark-300 border border-dark-400 rounded-lg px-3 py-2 text-white w-full"
                  />
                </div>
                <div className="flex gap-2 mt-1">
                  <Button
                    onClick={() => setIsEditingJob(false)}
                    variant="outline"
                    size="sm"
                    className="border-dark-400"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveJobInfo}
                    disabled={isSavingJob}
                    size="sm"
                    className="bg-gold-400 hover:bg-gold-500 text-dark-50"
                  >
                    {isSavingJob ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative flex items-center">
                <div className={cn(
                  'flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg',
                  isEditMode && isMyProfile ? (
                    isDarkMode 
                      ? 'bg-dark-300/50' 
                      : 'bg-light-300 text-light-900 border border-light-400'
                  ) : ''
                )}>
                  <Briefcase className="h-5 w-5 text-gold-400" />
                  <span className="text-sm sm:text-base">{jobTitle || 'Role'} @ {company || 'Company'}</span>
                </div>
                {isMyProfile && isEditMode && (
                  <div className="flex items-center">
                    <button
                      onClick={() => setIsEditingJob(true)}
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full ml-2",
                        isDarkMode 
                          ? "bg-dark-300 text-gold-400 hover:bg-dark-400" 
                          : "bg-light-300 text-gold-600 hover:bg-light-400 border border-light-400"
                      )}
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Clubs membership */}
          {isLoadingClubs ? (
            <div className="flex items-center justify-center text-dark-800 mt-2">
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              <span>Loading clubs...</span>
            </div>
          ) : userClubs.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-2 px-4">
              {userClubs.map((club) => (
                <button
                  key={club.club_id}
                  onClick={() => navigate(`/dashboard/clubs/${club.club_id}`)}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1 rounded-full text-sm',
                    'transition-colors',
                    club.is_primary
                      ? 'bg-gold-400/20 text-gold-400 hover:bg-gold-400/30'
                      : 'bg-dark-300 text-dark-800 hover:bg-dark-400 hover:text-white'
                  )}
                >
                  {club.logo ? (
                    <img
                      src={club.logo}
                      alt={club.club_name}
                      className={cn(
                        'w-4 h-4 rounded-full object-cover',
                        club.is_primary ? 'border border-gold-400' : 'border border-dark-400'
                      )}
                      onError={(e) => {
                        const img = e.currentTarget;
                        const shield = document.createElement('span');
                        shield.innerHTML =
                          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gold-400"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';
                        shield.className = club.is_primary ? 'text-gold-400' : 'text-dark-800';
                        img.parentNode?.replaceChild(shield.firstChild!, img);
                      }}
                    />
                  ) : (
                    <Shield
                      className={cn(
                        'h-4 w-4',
                        club.is_primary ? 'text-gold-400' : 'text-dark-800'
                      )}
                    />
                  )}
                  <span className="truncate max-w-[120px]">{club.club_name}</span>
                  {club.is_primary && <span className="w-1.5 h-1.5 bg-gold-400 rounded-full" />}
                </button>
              ))}
            </div>
          ) : clubDisplay ? (
            <div className="flex items-center justify-center text-dark-800 mt-1">
              <button
                onClick={() => navigate('/dashboard/profile?tab=clubs')}
                className="flex items-center hover:text-gold-400 transition-colors"
              >
                <Shield className="h-4 w-4 mr-1" />
                <span>{clubDisplay}</span>
              </button>
            </div>
          ) : null}

          {/* Add spacer to ensure content is above the edit button */}
          <div className="h-12 w-full"></div>


        {/* Sports editor modal */}
        {showSportsEditor && (
          <div className="fixed inset-0 z-50 bg-dark-50/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-2xl">
              <SportsSelector
                onClose={() => setShowSportsEditor(false)}
                onSportsUpdate={() => {
                  window.location.reload();
                }}
              />
            </div>
          </div>
        )}
      </div>
        
        {/* Edit Mode Toggle Button - Only for own profile */}
        {/* Placed in its own row at the very bottom */}
        {isMyProfile && (
          <div className="absolute bottom-0 right-0 p-4 sm:p-6">
            <Button
              onClick={toggleEditMode}
              variant={isEditMode ? "default" : "outline"}
              size="sm"
              className={cn(
                "flex items-center gap-1 sm:gap-2",
                isEditMode ? "bg-gold-400 text-dark-50" : "border-dark-400 text-white"
              )}
            >
              <Edit2 className="h-3 w-3" />
              <span className="truncate">{isEditMode ? "Exit Edit Mode" : "Edit Profile"}</span>
            </Button>
          </div>
        )}
        </div>
      </div>
  );
};

export default ProfileHeader;